import React from "react";
import { Link } from "react-router-dom";
import { ContactEmail, PolicyPage, PolicySection } from "./PolicyPage";

const policyLinks = [
  ["Privacy Policy", "/privacy", "Personal information, disclosures, retention, and your rights."],
  ["Privacy Choices", "/privacy-choices", "How to make a data request or marketing choice."],
  ["Cookie Policy", "/cookies", "Cookies, browser storage, pixels, and available controls."],
  ["Terms of Service", "/terms", "The rules that govern site use and purchases."],
  ["Shipping & Delivery", "/shipping", "Processing, transit, customs, tracking, and delivery issues."],
  ["Returns & Refunds", "/returns", "Eligibility, exclusions, return steps, and refund timing."],
  ["Orders & Cancellations", "/orders", "Acceptance, changes, cancellations, pre-orders, and limits."],
  ["Payments & Billing", "/payments", "Payment authorization, currency, taxes, and billing questions."],
  ["Product Information", "/product-policy", "Descriptions, color, materials, sizing, and availability."],
  ["Jewelry Care & Warranty", "/warranty-care", "Care guidance and limited manufacturing-defect coverage."],
  ["Promotions & Gift Cards", "/promotions", "Discount codes, offers, price adjustments, and gift cards."],
  ["Consumer Rights", "/consumer-rights", "Mandatory rights and region-specific cancellation information."],
  ["Acceptable Use", "/acceptable-use", "Conduct that is and is not permitted on the site."],
  ["Intellectual Property", "/intellectual-property", "Ownership, permitted use, and infringement reports."],
  ["Security & Fraud", "/security", "Transaction review, account safety, and scam reporting."],
  ["Marketing Communications", "/communications", "Email preferences, service messages, and unsubscribe choices."],
  ["Complaints & Disputes", "/complaints", "How to raise a concern and how we aim to respond."],
  ["Accessibility", "/accessibility", "Our accessibility commitment and how to report a barrier."],
];

export function PolicyHub() {
  return (
    <PolicyPage
      title="Policy Center"
      intro="Find the terms, customer-care rules, privacy information, and notices that apply when you visit or shop with Sacuddin."
    >
      <div className="policy-grid">
        {policyLinks.map(([title, path, description]) => (
          <section className="policy-card" key={path}>
            <h2>{title}</h2>
            <p>{description}</p>
            <Link to={path}>Read policy</Link>
          </section>
        ))}
      </div>
    </PolicyPage>
  );
}

export function OrdersPolicy() {
  return (
    <PolicyPage title="Order & Cancellation Policy" intro="How Sacuddin receives, reviews, accepts, changes, and cancels online orders.">
      <PolicySection title="1. Placing an order">
        <p>
          Check product, quantity, address, contact, and payment details before submitting.
          Your order is an offer to buy. A receipt or acknowledgment means we received it;
          acceptance occurs when we expressly confirm acceptance or dispatch the goods.
          Cart contents are not reserved and availability can change before acceptance.
        </p>
      </PolicySection>
      <PolicySection title="2. Verification and purchase limits">
        <p>
          We may verify contact, delivery, or payment details and request reasonable
          supporting information to prevent fraud. We may apply quantity limits per item,
          customer, household, payment method, or address. Orders that appear intended for
          unauthorized resale may be declined.
        </p>
      </PolicySection>
      <PolicySection title="3. Customer-requested changes or cancellation">
        <p>
          Email <ContactEmail subject="Order change or cancellation" /> as soon as
          possible with the order number. We will try to help, but cannot guarantee a
          change or cancellation after processing begins. If dispatch cannot be stopped,
          follow the <Link to="/returns">Returns &amp; Refunds Policy</Link>. Statutory
          cancellation rights remain unaffected.
        </p>
      </PolicySection>
      <PolicySection title="4. Cancellation by Sacuddin">
        <p>
          Before acceptance, we may decline or cancel because of stock, payment failure,
          shipping restrictions, legal requirements, fraud concerns, purchase limits, or
          an obvious price or product error. If funds were collected, the cancelled amount
          will be returned to the original payment method. We may offer an alternative,
          but will not substitute an item or charge more without your agreement.
        </p>
      </PolicySection>
      <PolicySection title="5. Split shipments and partial cancellation">
        <p>
          Items may ship separately. If part of an order becomes unavailable, we may ship
          the remaining items and refund the unavailable part, provided doing so does not
          materially change the transaction or conflict with applicable law. Any delivery
          charge adjustment will be explained.
        </p>
      </PolicySection>
      <PolicySection title="6. Pre-orders and back-orders">
        <p>
          When offered, an estimated availability date and any special payment timing will
          be shown on the product page or checkout. Estimates can change. If a material
          delay occurs, we will provide an update and any cancellation option required by
          law. Mixed orders may wait or ship separately.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export function PaymentsPolicy() {
  return (
    <PolicyPage title="Payment & Billing Policy" intro="Information about payment authorization, billing accuracy, currency, taxes, and refunds.">
      <PolicySection title="1. Accepted payment methods">
        <p>
          Payment methods actually accepted are those displayed at checkout. Availability
          may depend on device, currency, destination, and provider. You confirm that you
          are authorized to use the selected method and that billing information is
          complete and accurate.
        </p>
      </PolicySection>
      <PolicySection title="2. Authorization and capture">
        <p>
          We or a payment provider may place an authorization hold when an order is
          submitted. Funds may be captured immediately or when the order is accepted or
          dispatched. A declined or cancelled order can leave a temporary hold; only your
          bank controls when that hold disappears.
        </p>
      </PolicySection>
      <PolicySection title="3. Currency and conversion">
        <p>
          You will be charged in the currency shown in the final checkout total. A card
          issuer or payment provider may convert currency and add fees using its own rate
          and terms. Displayed conversions outside checkout may be estimates.
        </p>
      </PolicySection>
      <PolicySection title="4. Taxes, duties, and charges">
        <p>
          Applicable sales, consumption, or similar taxes may be calculated using the
          order and delivery information. Import duties, brokerage, and customs charges
          are governed by the <Link to="/shipping">Shipping &amp; Delivery Policy</Link>.
          We do not control fees charged independently by a bank, wallet, carrier, or
          government authority.
        </p>
      </PolicySection>
      <PolicySection title="5. Payment security and review">
        <p>
          Orders may be screened for fraud. We may request verification, limit attempts,
          or decline a transaction where reasonably necessary. Do not email full card
          numbers or security codes. Read our <Link to="/security">Security &amp; Fraud Policy</Link>.
        </p>
      </PolicySection>
      <PolicySection title="6. Refunds and billing concerns">
        <p>
          Approved refunds are sent to the original payment method unless the law requires
          another solution. Posting times depend on the provider. If you see an unfamiliar
          or incorrect charge, contact <ContactEmail subject="Billing question" /> promptly
          before starting a payment dispute so we can investigate; this does not restrict
          any right to contact your provider.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export function ProductPolicy() {
  return (
    <PolicyPage title="Product Information Policy" intro="How to read Sacuddin product descriptions, images, materials, sizing, pricing, and availability.">
      <PolicySection title="1. Descriptions and images">
        <p>
          We aim for accurate titles, images, dimensions, materials, and care guidance.
          Colors and scale can appear differently because of lighting, screen calibration,
          photography, and display settings. Packaging may change without altering the
          product. Refer to the written measurements rather than judging scale only from
          images.
        </p>
      </PolicySection>
      <PolicySection title="2. Craft and material variation">
        <p>
          Hand-finishing and natural or organic materials can create minor differences in
          tone, shape, texture, pearl surface, stone pattern, and placement. These
          characteristics are not defects when they fall within the product description
          and reasonable craftsmanship tolerances.
        </p>
      </PolicySection>
      <PolicySection title="3. Materials, plating, and sensitivities">
        <p>
          Review the material listing on each product page. Plating naturally changes with
          wear and exposure. If you have allergies or sensitivities, consult the listed
          materials and a qualified health professional before use. “Hypoallergenic,” if
          used, means designed to reduce common reactions and is not a guarantee that no
          reaction will occur.
        </p>
      </PolicySection>
      <PolicySection title="4. Sizing and fit">
        <p>
          Sizing guides are general aids. Fit depends on body measurements, design, and
          preference. Measure before ordering and contact us with questions. Altering or
          resizing an item after delivery may affect return and warranty eligibility.
        </p>
      </PolicySection>
      <PolicySection title="5. Availability and corrections">
        <p>
          Stock indicators are not a guarantee until an order is accepted. We may correct
          an obvious description, image, price, or inventory error. If a correction
          materially affects a submitted order, we will seek your agreement or cancel and
          refund the affected item.
        </p>
      </PolicySection>
      <PolicySection title="6. Safe use">
        <p>
          Jewelry is not a toy. Keep small parts and packaging away from children and
          pets. Remove jewelry before sleep, bathing, swimming, sport, or activities where
          it may catch or cause injury. Follow our <Link to="/warranty-care">Jewelry Care &amp; Limited Warranty</Link>.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export function WarrantyCarePolicy() {
  return (
    <PolicyPage title="Jewelry Care & Limited Warranty" intro="Practical care guidance and the coverage available for manufacturing defects.">
      <PolicySection title="1. Everyday care">
        <ul>
          <li>Put jewelry on after lotions, perfume, cosmetics, and hair products have dried.</li>
          <li>Remove it before bathing, swimming, exercise, sleep, cleaning, or contact with chemicals.</li>
          <li>After wear, wipe gently with a clean, soft, dry cloth. Do not use abrasive cloths, dips, toothpaste, or harsh cleaners.</li>
          <li>Store pieces separately in a dry pouch or lined box, away from heat, sunlight, and humidity.</li>
          <li>Handle stones, pearls, posts, clasps, and chains gently; do not bend or pull them.</li>
        </ul>
      </PolicySection>
      <PolicySection title="2. Plating, pearls, and stones">
        <p>
          Plating is a surface finish that gradually wears depending on skin chemistry,
          friction, moisture, and care. Pearls and some stones are porous and should be
          kept away from acids, sprays, and soaking. Normal changes in finish are wear,
          not necessarily a manufacturing defect.
        </p>
      </PolicySection>
      <PolicySection title="3. Limited warranty coverage">
        <p>
          For 90 days from delivery, Sacuddin will assess claims that an item had a
          manufacturing defect in materials or workmanship when delivered. If covered,
          we may repair, replace, or refund the item, depending on availability, cost,
          and the remedy required by law. This voluntary warranty is offered in addition
          to, and does not replace, statutory rights.
        </p>
      </PolicySection>
      <PolicySection title="4. Exclusions">
        <p>
          The voluntary warranty does not cover ordinary wear; gradual plating or color
          change; scratches; accidental damage; loss; theft; improper storage or cleaning;
          water or chemical exposure; stretched chains; bent posts; or alteration, repair,
          or resizing by someone not authorized by Sacuddin. An exclusion will not limit
          a mandatory remedy for a qualifying defect.
        </p>
      </PolicySection>
      <PolicySection title="5. Making a claim">
        <p>
          Email <ContactEmail subject="Warranty claim" /> with your order number, a
          description, and clear photos. Keep the item until we give instructions. We may
          request inspection and proof of purchase. Do not send an item without return
          authorization.
        </p>
      </PolicySection>
      <PolicySection title="6. Repairs outside coverage">
        <p>
          Contact us to ask whether a paid repair is available. Availability, estimate,
          shipping, and timing must be confirmed before sending an item. We cannot promise
          repair of every design or restoration to an original finish.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export function PromotionsPolicy() {
  return (
    <PolicyPage title="Promotions, Discounts & Gift Cards Policy" intro="General rules for promotional offers and, if made available, Sacuddin gift cards.">
      <PolicySection title="1. Promotional offers">
        <p>
          Each offer is subject to its advertised dates, territory, eligible products,
          customer or order requirements, and stock. Unless stated otherwise, one code may
          be used per order, codes cannot be combined, and discounts do not apply to prior
          purchases, delivery, taxes, duties, gift cards, or excluded products.
        </p>
      </PolicySection>
      <PolicySection title="2. Using a code">
        <p>
          Enter the code before submitting payment and check that the final total changed.
          We cannot guarantee a retroactive discount if the code was omitted, expired,
          ineligible, or entered incorrectly. An offer has no cash value and may not be
          sold, copied, altered, or obtained through misuse.
        </p>
      </PolicySection>
      <PolicySection title="3. Returns and discounted orders">
        <p>
          A refund is based on the amount actually paid for the returned item, allocated
          proportionally where an order-level discount applied. If a return causes the
          remaining order to fall below an offer threshold, the refund may be adjusted by
          the lost discount where the offer disclosed this and applicable law allows it.
        </p>
      </PolicySection>
      <PolicySection title="4. Price adjustments">
        <p>
          We do not generally adjust prices after purchase unless an offer expressly says
          otherwise or consumer law requires it. Temporary promotions do not mean an
          earlier price was incorrect.
        </p>
      </PolicySection>
      <PolicySection title="5. Gift cards, if offered">
        <p>
          Gift cards may be used only on the channel and in the currency stated when sold.
          They cannot be redeemed for cash except where law requires, reloaded, or replaced
          if lost or used without authorization unless the purchaser provides proof and we
          can disable the balance. Expiration and fee rules will comply with applicable law
          and be disclosed at purchase.
        </p>
      </PolicySection>
      <PolicySection title="6. Abuse and changes">
        <p>
          We may refuse or cancel benefits obtained through fraud, automated code testing,
          duplicate accounts, resale, or other misuse. We may end or correct an offer, but
          will honor already accepted qualifying orders unless cancellation is permitted
          by law and our <Link to="/orders">Order Policy</Link>.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export function AccessibilityStatement() {
  return (
    <PolicyPage title="Accessibility Statement" intro="Sacuddin wants visitors to be able to browse products, review policies, and shop with dignity and independence.">
      <PolicySection title="Our commitment">
        <p>
          We aim to improve the accessibility and usability of sacuddin.shop and to take
          recognized accessibility guidance into account when maintaining customer-facing
          content and interactions. Accessibility is an ongoing effort as products,
          content, browsers, assistive technologies, and standards change.
        </p>
      </PolicySection>
      <PolicySection title="Using the site">
        <p>
          The site is intended to support keyboard navigation, meaningful headings and
          labels, readable contrast, text alternatives for informative images, and zoom
          and reflow on common devices. Some third-party checkout, payment, map, or social
          features may be controlled by their providers.
        </p>
      </PolicySection>
      <PolicySection title="Need assistance or found a barrier?">
        <p>
          Email <ContactEmail subject="Accessibility assistance" /> with the page address,
          the task you were trying to complete, the barrier, and—if comfortable—the browser
          and assistive technology used. We will make reasonable efforts to provide the
          information or service through an accessible alternative and investigate the issue.
        </p>
      </PolicySection>
      <PolicySection title="Feedback">
        <p>
          We welcome specific accessibility feedback. Please do not include sensitive
          personal or full payment-card information in an accessibility message.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export function IntellectualPropertyNotice() {
  return (
    <PolicyPage title="Intellectual Property Notice" intro="Ownership, permitted use, and how to report content that may infringe intellectual property rights.">
      <PolicySection title="1. Sacuddin materials">
        <p>
          Sacuddin-created names, logos, product names, text, graphics, page layouts,
          photographs, videos, packaging artwork, and software are owned by or licensed to
          Sacuddin and protected by applicable copyright, trademark, design, and other laws.
          No license is granted except the limited permission below.
        </p>
      </PolicySection>
      <PolicySection title="2. Permitted personal use">
        <p>
          You may view the site and make a limited copy of an order record or policy for
          personal, non-commercial use. You may share an ordinary link to a public page.
          You may not remove notices, imply endorsement, frame the site, scrape a catalog,
          train a commercial model on protected content, reproduce product photography, or
          use materials in merchandise, advertising, or another store without written
          permission or a legal exception.
        </p>
      </PolicySection>
      <PolicySection title="3. Third-party rights">
        <p>
          Third-party names, marks, content, and services belong to their respective owners.
          Their appearance does not transfer rights or necessarily imply affiliation.
        </p>
      </PolicySection>
      <PolicySection title="4. Reporting infringement">
        <p>
          Send a good-faith report to <ContactEmail subject="Intellectual property report" />
          with your contact details, the protected work or mark, the exact site URL and
          material at issue, the basis of your rights, and a statement that the information
          is accurate. If acting for an owner, include authority to act. We may request
          additional information and will respond under applicable law.
        </p>
      </PolicySection>
      <PolicySection title="5. Feedback">
        <p>
          If you voluntarily provide an idea about the store without a separate written
          agreement, you allow us to use that feedback without restriction or payment,
          while you retain ownership of any rights you already hold. Do not send confidential
          business ideas through general customer-service channels.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export function AcceptableUsePolicy() {
  return (
    <PolicyPage title="Acceptable Use Policy" intro="Rules designed to keep Sacuddin's website, customers, and transactions safe.">
      <PolicySection title="Permitted use">
        <p>
          Use the site lawfully for browsing, purchasing, contacting support, and other
          features we make available. Provide accurate information and respect other users,
          our staff, and third-party rights.
        </p>
      </PolicySection>
      <PolicySection title="Prohibited activity">
        <p>You must not:</p>
        <ul>
          <li>break the law, infringe rights, harass, threaten, deceive, or submit unlawful or harmful content;</li>
          <li>use stolen credentials or payment methods, evade purchase limits, test cards, commit chargeback fraud, or obtain promotions dishonestly;</li>
          <li>introduce malware, probe vulnerabilities without written authorization, bypass security, interfere with service, or access non-public systems or data;</li>
          <li>use bots or automated tools to overload the site, hoard stock, scrape protected content, or make purchases except through an expressly permitted interface;</li>
          <li>impersonate another person or Sacuddin, falsify headers, or misrepresent affiliation; or</li>
          <li>resell site access or use the site to build a competing catalog in violation of applicable rights.</li>
        </ul>
      </PolicySection>
      <PolicySection title="Enforcement">
        <p>
          We may investigate, preserve evidence, limit activity, cancel affected orders,
          suspend access, and report conduct to payment providers or authorities when
          reasonably necessary and lawful. Enforcement will be proportionate to the risk
          and does not limit other remedies.
        </p>
      </PolicySection>
      <PolicySection title="Security research and reports">
        <p>
          Do not test the live site without prior written authorization. Report a suspected
          vulnerability privately to <ContactEmail subject="Security report" /> and avoid
          accessing, changing, retaining, or disclosing other people’s data.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export function SecurityFraudPolicy() {
  return (
    <PolicyPage title="Security & Fraud Prevention Policy" intro="How transaction review works and what customers can do to protect their information.">
      <PolicySection title="1. Our approach">
        <p>
          We use reasonable safeguards and may work with payment, hosting, and fraud-
          prevention providers to identify unusual activity. No system can be guaranteed
          completely secure, and security controls may change in response to risk.
        </p>
      </PolicySection>
      <PolicySection title="2. Order review">
        <p>
          A transaction may be delayed, limited, or declined when details do not match,
          attempts appear automated, a payment provider rejects authorization, or activity
          presents unusual risk. We may ask you to confirm limited order details. Do not
          send a full card number, password, or security code by email.
        </p>
      </PolicySection>
      <PolicySection title="3. Protecting yourself">
        <ul>
          <li>Use a unique password if an account feature is available, and protect access to your email.</li>
          <li>Confirm that you are visiting sacuddin.shop before entering information.</li>
          <li>Do not pay by an unlisted method in response to an unsolicited message.</li>
          <li>Review payment statements and delivery notifications promptly.</li>
          <li>Contact your payment provider immediately if credentials may be compromised.</li>
        </ul>
      </PolicySection>
      <PolicySection title="4. Official communications">
        <p>
          Sacuddin will not ask by email for your complete card number, card security code,
          or account password. Be cautious of urgency, spelling variations, unexpected
          attachments, and requests to move a conversation or payment away from the site.
        </p>
      </PolicySection>
      <PolicySection title="5. Reporting suspicious activity">
        <p>
          Forward details to <ContactEmail subject="Suspected fraud or security issue" />.
          Include the order number, sender address, relevant URL, and screenshots, but
          redact payment credentials and government identifiers. Security incidents
          involving personal information are handled in line with applicable notification law.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export function ConsumerRightsPolicy() {
  return (
    <PolicyPage title="Consumer Rights & Regional Notices" intro="A summary of mandatory rights that may apply in addition to Sacuddin's store policies.">
      <p className="policy-notice">
        This page is a general summary, not a waiver or complete statement of local law.
        If a store policy conflicts with a non-waivable consumer right, that right controls.
      </p>
      <PolicySection title="1. Legal guarantees">
        <p>
          Goods may be covered by mandatory guarantees that they match their description,
          are of acceptable quality, and are fit for a disclosed purpose. Depending on the
          jurisdiction and seriousness of a problem, remedies may include repair,
          replacement, price reduction, cancellation, or refund. Voluntary warranty and
          return periods do not shorten those rights.
        </p>
      </PolicySection>
      <PolicySection title="2. Online cancellation rights">
        <p>
          In some countries, consumers may cancel many online purchases within a statutory
          cooling-off period after delivery without giving a reason. Exceptions often apply
          to personalized goods and sealed goods unsuitable for return for health or hygiene
          reasons once unsealed. You may need to notify us within the legal period and return
          the goods within a further deadline.
        </p>
        <p>
          To exercise a right, email <ContactEmail subject="Statutory cancellation" /> with
          your name, order number, the items, the date received, and a clear statement that
          you wish to cancel. We will explain the return steps and any cost allocation
          permitted by law.
        </p>
      </PolicySection>
      <PolicySection title="3. European Economic Area and United Kingdom">
        <p>
          Eligible consumers generally have a 14-day withdrawal period for many distance
          purchases, subject to legal exceptions. After notifying the seller, goods
          generally must be returned within 14 days. Reasonable handling beyond what is
          needed to inspect goods may reduce a refund. The statutory rules in the customer’s
          country control where they provide additional protection.
        </p>
      </PolicySection>
      <PolicySection title="4. Australia and New Zealand">
        <p>
          Consumer guarantees and remedies under applicable national law cannot be excluded.
          A major failure may entitle a consumer to reject goods and choose a refund or
          replacement; other failures may first be remedied within a reasonable time.
        </p>
      </PolicySection>
      <PolicySection title="5. United States and Canada">
        <p>
          Warranty, refund, automatic-renewal, tax, shipping, and privacy rights vary by
          state or province. Nothing in our policies excludes a right that local law makes
          non-waivable. Residents can also review the regional section of our <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </PolicySection>
      <PolicySection title="6. Complaints">
        <p>
          See our <Link to="/complaints">Complaints &amp; Dispute Resolution Policy</Link>.
          You may contact a consumer authority, data-protection authority, court, or other
          body where applicable; contacting us first does not remove those rights.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export function PrivacyChoices() {
  return (
    <PolicyPage title="Privacy Choices" intro="How to manage marketing, cookies, and requests concerning your personal information.">
      <PolicySection title="1. Make a privacy request">
        <p>
          Email <ContactEmail subject="Privacy request" /> with “Privacy Request” in the
          subject. State your country or state, the email used with Sacuddin, and whether
          you seek access, correction, deletion, portability, restriction, objection,
          opt-out, or appeal. Do not send identity documents unless we specifically request
          them through an appropriate method.
        </p>
      </PolicySection>
      <PolicySection title="2. Verification and authorized agents">
        <p>
          To protect customers, we may compare request details with our records or ask for
          additional verification. We will use verification information only for the
          request. An authorized agent may act where law permits; we may ask for signed
          authority and may verify directly with the individual.
        </p>
      </PolicySection>
      <PolicySection title="3. Marketing choices">
        <p>
          Use the unsubscribe link in a promotional email or contact us. We may retain a
          minimal suppression record so we do not add you back. Order confirmations,
          delivery notices, security alerts, policy notices, and responses you request are
          service messages and may still be sent.
        </p>
      </PolicySection>
      <PolicySection title="4. Cookies and targeted advertising">
        <p>
          Use any cookie preference tool displayed on the site, browser controls, and a
          recognized browser opt-out signal where supported. Choices are often tied to a
          particular browser or device. Read the <Link to="/cookies">Cookie Policy</Link>.
        </p>
      </PolicySection>
      <PolicySection title="5. Timing and appeals">
        <p>
          We aim to respond within the period required by applicable law. Complex or
          repeated requests may take longer where permitted, and lawful exceptions may
          apply. If we deny a request, we will explain the available reason and appeal or
          complaint route where required.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export function CommunicationsPolicy() {
  return (
    <PolicyPage title="Marketing Communications Policy" intro="How Sacuddin handles promotional email, customer-service messages, preferences, and unsubscribe requests.">
      <PolicySection title="1. Promotional messages">
        <p>
          We may send product news, offers, or other marketing when you request it or when
          another lawful basis permits it. We do not require marketing consent as a
          condition of completing an ordinary purchase unless the communication is
          necessary for that purchase.
        </p>
      </PolicySection>
      <PolicySection title="2. Service messages">
        <p>
          Order receipts, payment updates, delivery notices, recall or safety notices,
          customer-service responses, security alerts, and material policy updates are
          transactional or service communications. You may receive them even if you opt
          out of marketing while an order or customer request is active.
        </p>
      </PolicySection>
      <PolicySection title="3. Unsubscribe and preferences">
        <p>
          Use the unsubscribe link in a promotional email or contact
          <ContactEmail subject="Marketing opt-out" />. Allow a reasonable processing
          period and any period set by law. You may continue to see general advertising
          that is not based on an email subscription.
        </p>
      </PolicySection>
      <PolicySection title="4. List integrity">
        <p>
          Do not subscribe another person without permission. We may use confirmation,
          suppression, bounce, and complaint records to maintain a lawful and accurate
          list. We do not sell subscriber lists for money.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export function ComplaintsPolicy() {
  return (
    <PolicyPage title="Complaints & Dispute Resolution Policy" intro="How to raise an order, product, service, privacy, or accessibility concern with Sacuddin.">
      <PolicySection title="1. Contact us first">
        <p>
          Email <ContactEmail subject="Formal complaint" /> with your name, order number
          if applicable, a clear description, relevant dates, supporting photographs or
          documents, and the outcome you are seeking. Do not send complete payment-card
          details, passwords, or unnecessary sensitive information.
        </p>
      </PolicySection>
      <PolicySection title="2. What happens next">
        <p>
          We aim to acknowledge a complaint within five business days and provide a
          substantive response within 15 business days. Complex matters, carrier or
          provider investigations, and public holidays may require more time. If so, we
          will try to explain the delay and next step. These are service targets, not a
          waiver or extension of legal deadlines.
        </p>
      </PolicySection>
      <PolicySection title="3. Review and evidence">
        <p>
          We may review order, delivery, payment, correspondence, and site records and ask
          for reasonable evidence. Privacy and security complaints will be handled with
          restricted access appropriate to the issue.
        </p>
      </PolicySection>
      <PolicySection title="4. Escalation">
        <p>
          If you disagree with the response, reply and request a further review, explaining
          what remains unresolved. Where applicable, you may also use a payment-provider
          process, consumer authority, data-protection authority, alternative dispute
          process, or court with jurisdiction. Nothing here requires arbitration or limits
          a mandatory remedy.
        </p>
      </PolicySection>
      <PolicySection title="5. Good-faith resolution">
        <p>
          We ask both sides to communicate accurately and preserve relevant records while
          a matter is reviewed. Retaliation for making a good-faith complaint or exercising
          a consumer or privacy right is not permitted.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}
